import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { PasswordInput } from '../../components/ui/password-input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTPVerify, setShowTOTPVerify] = useState(false)
  const [showTOTPEnroll, setShowTOTPEnroll] = useState(false)
  const [isUserBanned, setIsUserBanned] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsUserBanned(false)
    setIsLoading(true)
    try {
      await login(email, password)
      
      // Always check for MFA factors after successful password authentication
      const { data: factors } = await supabase.auth.mfa.listFactors()
      
      console.log('[Login] Password authentication successful, checking TOTP factors:', factors)
      
      // Check for verified TOTP factors only
      const verifiedTotpFactors = factors?.totp?.filter(factor => factor.status === 'verified') || []
      
      if (verifiedTotpFactors.length > 0) {
        // User has verified TOTP set up, require verification
        console.log('[Login] Verified TOTP factor found, requiring verification')
        setShowTOTPVerify(true)
      } else {
        // User needs to set up TOTP first (or complete incomplete enrollment)
        console.log('[Login] No verified TOTP factor found, requiring enrollment')
        setShowTOTPEnroll(true)
      }
      
      // Never redirect directly - always require MFA verification/enrollment
    } catch (error: unknown) {
      let errorMessage = 'Failed to login'
      
      // Check if this is a user_banned error
      if (error instanceof Error) {
        errorMessage = error.message
        // Check if the error message contains user_banned or parse JSON response
        try {
          const errorData = JSON.parse(error.message)
          if (errorData.code === 'user_banned') {
            setIsUserBanned(true)
          }
        } catch {
          // If error message is not JSON, check if it contains user_banned
          if (error.message.includes('user_banned') || error.message.includes('User is banned')) {
            setIsUserBanned(true)
          }
        }
      }
      
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`flex h-screen w-full items-center justify-center ${getThemeClass('gradients.primary')}`}>
      {showTOTPVerify ? (
        <TOTPVerify
          redirectTo="/admin"
          onComplete={() => {
            console.log('[Login] TOTP verification complete, redirecting to admin dashboard')
            navigate('/admin', { replace: true })
          }}
        />
      ) : showTOTPEnroll ? (
        <TOTPEnrollment
          onComplete={async () => {
            console.log('[Login] TOTP enrollment complete, redirecting to admin dashboard')
            navigate('/admin', { replace: true })
          }}
        />
      ) : (
        <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
          <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Login</h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            <div>
              <PasswordInput
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            {formError && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className={`w-full ${getThemeClass('components.button.primary')}`}
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
            {!isUserBanned && (
              <div className="mt-4 text-center">
                <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>
        </Card>
      )}
    </div>
  )
}
